import { useState } from 'react';
import { AttentionTriangleIcon, CaretIcon } from '@rsos/assets/icons';
import type {
  ConsistCar,
  NorfolkSouthernTrainSnapshot,
} from '@rsos/reduxDataStore/src/features/resourceRequests';
import { formatTrainLabel } from '../../../../../utils/norfolkSouthern/trains';
import ConsistCarRow from './ConsistCarRow';
import styles from './NorfolkSouthernStatus.module.css';
import { getHazmatCars } from './norfolkSouthernStatusUtils';

interface ConsistCarSectionsProps {
  cars: ConsistCar[];
  isFailed: boolean;
  snapshot: NorfolkSouthernTrainSnapshot;
}

/**
 * The collapsible hazmat and all-cars lists, or a note when Norfolk Southern
 * reported no equipment.
 */
function ConsistCarSections({
  cars,
  isFailed,
  snapshot,
}: ConsistCarSectionsProps): JSX.Element {
  const [isHazmatExpanded, setIsHazmatExpanded] = useState(true);
  const [isAllCarsExpanded, setIsAllCarsExpanded] = useState(true);
  const hazmatCars = getHazmatCars(cars);

  return (
    <>
      {cars.length > 0 && (
        <div
          className={styles.carSections}
          data-name="norfolk-southern-car-sections"
        >
          {hazmatCars.length > 0 && (
            <>
              <button
                type="button"
                className={styles.hazmatHeader}
                aria-expanded={isHazmatExpanded}
                onClick={() => setIsHazmatExpanded(prev => !prev)}
                data-name="norfolk-southern-hazmat-header"
              >
                <span className={styles.hazmatTitle}>
                  <AttentionTriangleIcon
                    color="background"
                    height={14}
                    width={14}
                  />
                  Hazardous Car Information
                </span>
                <span
                  className={`${styles.caret}${isHazmatExpanded ? '' : ` ${styles.caretCollapsed}`}`}
                >
                  <CaretIcon color="background" width={11} height={8} />
                </span>
              </button>
              {isHazmatExpanded && (
                <div
                  className={styles.carList}
                  data-name="norfolk-southern-hazmat-cars"
                >
                  {hazmatCars.map(car => (
                    <ConsistCarRow
                      key={`hazmat-${car.sequence ?? car.reportingMarks}`}
                      car={car}
                    />
                  ))}
                </div>
              )}
            </>
          )}

          <button
            type="button"
            className={styles.sectionTitle}
            aria-expanded={isAllCarsExpanded}
            onClick={() => setIsAllCarsExpanded(prev => !prev)}
            data-name="norfolk-southern-all-cars-toggle"
          >
            All Cars ({cars.length})
            <span
              className={`${styles.caret}${isAllCarsExpanded ? '' : ` ${styles.caretCollapsed}`}`}
            >
              <CaretIcon color="primaryText" width={11} height={8} />
            </span>
          </button>
          {isAllCarsExpanded && (
            <div
              className={styles.carList}
              data-name="norfolk-southern-all-cars"
            >
              {cars.map(car => (
                <ConsistCarRow
                  key={`car-${car.sequence ?? car.reportingMarks}`}
                  car={car}
                />
              ))}
            </div>
          )}
        </div>
      )}
      {!isFailed && cars.length === 0 && (
        <div className={styles.errorText} data-name="norfolk-southern-no-cars">
          Norfolk Southern reported no equipment for{' '}
          {formatTrainLabel(snapshot)}.
        </div>
      )}
    </>
  );
}

export default ConsistCarSections;
